import { ref, update, get } from "firebase/database";
import { db } from "../configs/Firebase";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { app } from "../configs/Firebase";

const GRAPH_API_BASE = "https://graph.microsoft.com/v1.0";

/**
 * Fetch user profile photo from Microsoft Graph
 * @param {string} accessToken - Microsoft OAuth access token
 * @returns {Promise<Blob|null>} - Photo blob or null if not available
 */
export async function fetchUserPhoto(accessToken) {
  try {
    const response = await fetch(`${GRAPH_API_BASE}/me/photo/$value`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (response.status === 404) {
      // User has no profile photo
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to fetch photo: ${response.status} ${response.statusText}`);
    }

    // Get binary image data
    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error("Error fetching user photo:", error);
    return null;
  }
}

/**
 * Fetch organization branding/logo from Microsoft Graph
 * @param {string} accessToken - Microsoft OAuth access token
 * @returns {Promise<{logo: Blob|null, tenantId: string|null}>}
 */
export async function fetchOrganizationBranding(accessToken) {
  try {
    // First, get organization info to find tenant ID
    const orgResponse = await fetch(`${GRAPH_API_BASE}/organization`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!orgResponse.ok) {
      // User might be a personal account or lack permissions
      return { logo: null, tenantId: null };
    }

    const orgData = await orgResponse.json();
    const organizations = orgData.value || [];
    
    if (organizations.length === 0) {
      // Personal Microsoft account
      return { logo: null, tenantId: null };
    }

    // Use the first organization (user's primary tenant)
    const tenantId = organizations[0].id;

    // Try to fetch organization branding
    try {
      const brandingResponse = await fetch(
        `${GRAPH_API_BASE}/organization/${tenantId}/branding/localizations/default`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (brandingResponse.ok) {
        const branding = await brandingResponse.json();
        
        // Fetch the logo if available
        if (branding.squareLogo) {
          const logoResponse = await fetch(
            `${GRAPH_API_BASE}/organization/${tenantId}/branding/localizations/default/squareLogo`,
            {
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          if (logoResponse.ok) {
            const logoBlob = await logoResponse.blob();
            return { logo: logoBlob, tenantId };
          }
        }
      }
    } catch (brandingError) {
      // Organization branding might not be available or require admin permissions
      console.warn("Could not fetch organization branding:", brandingError);
    }

    return { logo: null, tenantId };
  } catch (error) {
    console.error("Error fetching organization branding:", error);
    return { logo: null, tenantId: null };
  }
}

/**
 * Fetch user profile information from Microsoft Graph
 * @param {string} accessToken - Microsoft OAuth access token
 * @returns {Promise<{displayName: string|null, givenName: string|null, surname: string|null, mail: string|null}>}
 */
export async function fetchUserProfile(accessToken) {
  console.log("[fetchUserProfile] Starting to fetch user profile from Microsoft Graph");
  try {
    const response = await fetch(`${GRAPH_API_BASE}/me`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unable to read error response');
      console.error(`[fetchUserProfile] API request failed: ${response.status} ${response.statusText}`, errorText);
      throw new Error(`Failed to fetch user profile: ${response.status} ${response.statusText}`);
    }

    const profileData = await response.json();
    console.log("[fetchUserProfile] Received profile data from Microsoft Graph:", {
      displayName: profileData.displayName,
      givenName: profileData.givenName,
      surname: profileData.surname,
      mail: profileData.mail,
      userPrincipalName: profileData.userPrincipalName,
      fullResponse: profileData
    });
    
    const result = {
      displayName: profileData.displayName || null,
      givenName: profileData.givenName || null,
      surname: profileData.surname || null,
      mail: profileData.mail || profileData.userPrincipalName || null,
    };
    
    console.log("[fetchUserProfile] Extracted profile data:", result);
    return result;
  } catch (error) {
    console.error("[fetchUserProfile] Error fetching user profile:", error);
    return {
      displayName: null,
      givenName: null,
      surname: null,
      mail: null,
    };
  }
}

/**
 * Upload image to Firebase Storage and get download URL
 * @param {Blob} blob - Image blob
 * @param {string} userId - Firebase user ID
 * @param {string} type - 'photo' or 'orgLogo'
 * @returns {Promise<string>} - Download URL
 */
async function uploadImageToStorage(blob, userId, type) {
  const storage = getStorage(app);
  const extension = blob.type.split("/")[1] || "jpg";
  const filename = `${type}.${extension}`;
  const storagePath = `userData/${userId}/${filename}`;
  const imageRef = storageRef(storage, storagePath);

  await uploadBytes(imageRef, blob);
  const downloadURL = await getDownloadURL(imageRef);
  return downloadURL;
}

/**
 * Main function to fetch and cache Microsoft profile data
 * @param {string} userId - Firebase user ID
 * @param {string} accessToken - Microsoft OAuth access token
 */
export async function fetchMicrosoftProfileData(userId, accessToken) {
  console.log("[fetchMicrosoftProfileData] Starting to fetch Microsoft profile data for user:", userId);
  try {
    const [photoBlob, branding, userProfile] = await Promise.all([
      fetchUserPhoto(accessToken),
      fetchOrganizationBranding(accessToken),
      fetchUserProfile(accessToken),
    ]);

    console.log("[fetchMicrosoftProfileData] Fetched data:", {
      hasPhoto: !!photoBlob,
      hasBranding: !!branding.logo,
      userProfile: userProfile
    });

    const updates = {};

    // Handle user profile data (display name, etc.)
    // Use displayName if available, otherwise construct from givenName + surname
    let displayNameToStore = userProfile.displayName;
    if (!displayNameToStore && (userProfile.givenName || userProfile.surname)) {
      // Construct displayName from givenName + surname
      const nameParts = [userProfile.givenName, userProfile.surname].filter(Boolean);
      displayNameToStore = nameParts.join(" ").trim() || null;
      console.log("[fetchMicrosoftProfileData] Constructed displayName from givenName + surname:", displayNameToStore);
    }

    if (displayNameToStore) {
      const displayNamePath = "userData/" + userId + "/profile/displayName";
      updates[displayNamePath] = displayNameToStore;
      console.log("[fetchMicrosoftProfileData] Adding displayName to updates:", {
        path: displayNamePath,
        value: displayNameToStore
      });
    } else {
      console.warn("[fetchMicrosoftProfileData] No displayName available (neither from API nor constructed):", userProfile);
    }

    // Handle user photo
    if (photoBlob) {
      try {
        // Upload to Firebase Storage
        const photoURL = await uploadImageToStorage(photoBlob, userId, "photo");
        updates["userData/" + userId + "/profile/microsoftPhotoURL"] = photoURL;
        console.log("[fetchMicrosoftProfileData] Added photo URL to updates");
      } catch (error) {
        console.error("[fetchMicrosoftProfileData] Error uploading user photo:", error);
      }
    }

    // Handle organization logo
    if (branding.logo) {
      try {
        const logoURL = await uploadImageToStorage(branding.logo, userId, "orgLogo");
        updates["userData/" + userId + "/profile/microsoftOrgLogoURL"] = logoURL;
        console.log("[fetchMicrosoftProfileData] Added org logo URL to updates");
      } catch (error) {
        console.error("[fetchMicrosoftProfileData] Error uploading org logo:", error);
      }
    }

    // Store tenant ID if available
    if (branding.tenantId) {
      updates["userData/" + userId + "/profile/microsoftTenantId"] = branding.tenantId;
      console.log("[fetchMicrosoftProfileData] Added tenant ID to updates");
    }

    // Update Firebase Realtime Database
    if (Object.keys(updates).length > 0) {
      console.log("[fetchMicrosoftProfileData] Preparing to update Firebase with:", updates);
      console.log("[fetchMicrosoftProfileData] Update paths:", Object.keys(updates));
      try {
        const dbRef = ref(db);
        await update(dbRef, updates);
        console.log("[fetchMicrosoftProfileData] Successfully updated Firebase database");
        
        // Verify the update by reading back the displayName
        if (updates["userData/" + userId + "/profile/displayName"]) {
          const verifyRef = ref(db, "userData/" + userId + "/profile/displayName");
          const snapshot = await get(verifyRef);
          if (snapshot.exists()) {
            console.log("[fetchMicrosoftProfileData] Verified displayName in Firebase:", snapshot.val());
          } else {
            console.warn("[fetchMicrosoftProfileData] Warning: displayName not found in Firebase after update");
          }
        }
      } catch (updateError) {
        console.error("[fetchMicrosoftProfileData] Error updating Firebase:", updateError);
        console.error("[fetchMicrosoftProfileData] Update object that failed:", updates);
        throw updateError;
      }
    } else {
      console.warn("[fetchMicrosoftProfileData] No updates to apply to Firebase");
    }
  } catch (error) {
    console.error("[fetchMicrosoftProfileData] Error in fetchMicrosoftProfileData:", error);
    throw error;
  }
}

